import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Button, Spin, Alert, Empty, Tag, Badge, Drawer, Modal, Form, Select, DatePicker, TimePicker, Input, message } from 'antd';
import { PlusOutlined, ReloadOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { professorAulasDadasService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import AulaDadaProfessorCard from './AulaDadaProfessorCard';
import '../../styles/PortalProfessor.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;
const { TextArea } = Input;

const FORMATO_API = 'YYYY-MM-DD';

const STATUS_INFO = {
  PENDENTE: { color: 'gold', text: 'Pendente' },
  VALIDADA: { color: 'green', text: 'Validada' },
  REJEITADA: { color: 'red', text: 'Rejeitada' },
};

const STATUS_OPCOES = [
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'VALIDADA', label: 'Validada' },
  { value: 'REJEITADA', label: 'Rejeitada' },
];

// Portal do Professor -- Aulas Dadas (Fase 14B + filtros da Fase 14E +
// redesign mobile-first da Fase 14E-visual). O professor só visualiza e
// registra as próprias aulas; não há ação de editar/excluir/validar
// nesta tela -- essas superfícies simplesmente não existem aqui (o
// backend também não as expõe neste router). A visibilidade do botão
// "Registrar aula" vem de RBAC real (usePermissaoRecurso('aulas_dadas').podeCriar),
// nunca de user.role === 'PROFESSOR'.
//
// Filtros: dois estados deliberadamente separados -- `filtros*` (o que
// está selecionado nos controles, "em edição") e `filtrosAplicados` (o
// conjunto realmente usado na última consulta). Só o clique em "Filtrar"
// copia edição -> aplicado e dispara a requisição; alterar um
// Select/DatePicker sozinho nunca dispara nada. Isso garante que
// registrar uma aula sempre recarrega com os filtros que estavam de
// fato em vigor, nunca com edições ainda não confirmadas. Esta regra é
// idêntica à da Fase 14E funcional -- o redesign visual só muda ONDE e
// COMO os mesmos controles são exibidos (Drawer compacto em vez de
// barra aberta), nunca a lógica de quando a consulta é disparada.
const PortalProfessorAulasDadas = () => {
  const { podeCriar } = usePermissaoRecurso('aulas_dadas');

  const [aulas, setAulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // { status, message } | null

  const [turmas, setTurmas] = useState([]);
  const [carregandoTurmas, setCarregandoTurmas] = useState(true);

  // Filtros em edição (controles dentro do Drawer) -- nunca usados
  // diretamente na consulta.
  const [filtroDataInicial, setFiltroDataInicial] = useState(null);
  const [filtroDataFinal, setFiltroDataFinal] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(undefined);
  const [filtroTurmaId, setFiltroTurmaId] = useState(undefined);

  // Filtros aplicados (o que foi de fato usado na última consulta).
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [filtrosDrawerAberto, setFiltrosDrawerAberto] = useState(false);

  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(null);
  const [form] = Form.useForm();

  const carregarAulas = useCallback(async (params) => {
    setLoading(true);
    setErro(null);
    try {
      const response = await professorAulasDadasService.listar(params);
      setAulas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const status = err?.response?.status;
      const { message: mensagem } = buildApiErrorMessage(
        err,
        'Não foi possível carregar suas aulas dadas. Tente novamente.',
        { context: status === 401 ? 'session' : undefined }
      );
      setErro({ status, message: mensagem });
      setAulas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarTurmas = useCallback(async () => {
    setCarregandoTurmas(true);
    try {
      const response = await professorAulasDadasService.listarTurmasDisponiveis();
      setTurmas(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setTurmas([]);
      message.error('Não foi possível carregar suas turmas. Tente novamente.');
    } finally {
      setCarregandoTurmas(false);
    }
  }, []);

  useEffect(() => {
    // Turmas carregadas UMA vez ao montar a página -- reutilizadas tanto
    // pelo filtro de Turma quanto pelo modal "Registrar aula" (nunca uma
    // segunda chamada ao abrir o modal).
    carregarTurmas();
    carregarAulas({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltrar = () => {
    if (filtroDataInicial && filtroDataFinal && filtroDataInicial.isAfter(filtroDataFinal, 'day')) {
      message.error('Data inicial não pode ser maior que data final.');
      return;
    }

    const params = {};
    if (filtroDataInicial) params.data_inicial = filtroDataInicial.format(FORMATO_API);
    if (filtroDataFinal) params.data_final = filtroDataFinal.format(FORMATO_API);
    if (filtroStatus) params.status = filtroStatus;
    if (filtroTurmaId) params.turma_id = filtroTurmaId;

    setFiltrosAplicados(params);
    carregarAulas(params);
    setFiltrosDrawerAberto(false);
  };

  const handleLimparFiltros = () => {
    setFiltroDataInicial(null);
    setFiltroDataFinal(null);
    setFiltroStatus(undefined);
    setFiltroTurmaId(undefined);
    setFiltrosAplicados({});
    carregarAulas({});
    setFiltrosDrawerAberto(false);
  };

  const abrirModal = () => {
    form.resetFields();
    setErroSalvar(null);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setErroSalvar(null);
  };

  const handleTurmaChange = (turmaId) => {
    const turma = turmas.find((t) => t.id === turmaId);
    if (turma) {
      form.setFieldsValue({
        hora_inicio: turma.hora_inicio ? dayjs(turma.hora_inicio, 'HH:mm:ss') : null,
        hora_fim: turma.hora_fim ? dayjs(turma.hora_fim, 'HH:mm:ss') : null,
      });
    }
  };

  const handleSubmit = async (values) => {
    setSalvando(true);
    setErroSalvar(null);
    try {
      await professorAulasDadasService.registrar({
        id_turma: values.id_turma,
        data_aula: values.data_aula.format(FORMATO_API),
        hora_inicio: values.hora_inicio.format('HH:mm:ss'),
        hora_fim: values.hora_fim.format('HH:mm:ss'),
        observacoes: values.observacoes || null,
      });
      message.success('Aula registrada com sucesso.');
      setModalAberto(false);
      // Recarrega com os filtros APLICADOS atuais -- nunca com edições
      // ainda não confirmadas via "Filtrar".
      carregarAulas(filtrosAplicados);
    } catch (err) {
      const { message: mensagem } = buildApiErrorMessage(
        err,
        'Não foi possível registrar a aula. Verifique os dados e tente novamente.'
      );
      setErroSalvar(mensagem);
    } finally {
      setSalvando(false);
    }
  };

  // Resumo visual (chips informativos) dos filtros aplicados -- somente
  // apresentação, nunca removíveis individualmente nesta rodada (para
  // não introduzir uma nova regra de interação além do já aprovado:
  // "Limpar filtros" continua sendo a única forma de desfazer).
  const chipsFiltrosAplicados = [];
  if (filtrosAplicados.data_inicial || filtrosAplicados.data_final) {
    const ini = filtrosAplicados.data_inicial ? dayjs(filtrosAplicados.data_inicial).format('DD/MM') : '…';
    const fim = filtrosAplicados.data_final ? dayjs(filtrosAplicados.data_final).format('DD/MM') : '…';
    chipsFiltrosAplicados.push(`${ini} - ${fim}`);
  }
  if (filtrosAplicados.status) {
    chipsFiltrosAplicados.push(STATUS_INFO[filtrosAplicados.status]?.text || filtrosAplicados.status);
  }
  if (filtrosAplicados.turma_id) {
    const turma = turmas.find((t) => t.id === filtrosAplicados.turma_id);
    chipsFiltrosAplicados.push(turma ? turma.nome_turm : `Turma #${filtrosAplicados.turma_id}`);
  }
  const algumFiltroAplicado = chipsFiltrosAplicados.length > 0;

  const renderConteudo = () => {
    if (loading) {
      return (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando suas aulas dadas...</Text>
        </div>
      );
    }

    if (erro) {
      return (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 ? 'warning' : 'error'}
            showIcon
            message={erro.status === 403 ? 'Acesso indisponível' : 'Não foi possível carregar'}
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && (
            <Button icon={<ReloadOutlined />} onClick={() => carregarAulas(filtrosAplicados)}>
              Tentar novamente
            </Button>
          )}
        </div>
      );
    }

    if (aulas.length === 0) {
      return (
        <div className="portal-professor__state">
          <Empty
            description={
              algumFiltroAplicado
                ? 'Nenhuma aula encontrada para os filtros selecionados.'
                : 'Nenhuma aula dada registrada ainda.'
            }
          />
          {algumFiltroAplicado && (
            <Button icon={<ClearOutlined />} onClick={handleLimparFiltros}>
              Limpar filtros
            </Button>
          )}
        </div>
      );
    }

    return (
      <>
        <Text type="secondary" className="portal-professor__resultado-contagem">
          {aulas.length} {aulas.length === 1 ? 'aula encontrada' : 'aulas encontradas'}
        </Text>
        <div className="portal-professor__list">
          {aulas.map((aula) => (
            <AulaDadaProfessorCard key={aula.id} aula={aula} />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="portal-professor">
      <div className="portal-professor__header">
        <div>
          <Title level={3} className="portal-professor__title">
            Aulas Dadas
          </Title>
          <Text type="secondary" className="portal-professor__subtitle">
            Suas aulas registradas
          </Text>
        </div>
        {podeCriar && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={abrirModal}
            className="portal-professor__btn-registrar"
          >
            Registrar aula
          </Button>
        )}
      </div>

      <div className="portal-professor__filtros-resumo">
        <Badge count={chipsFiltrosAplicados.length} size="small" offset={[-4, 4]}>
          <Button icon={<FilterOutlined />} onClick={() => setFiltrosDrawerAberto(true)}>
            Filtros
          </Button>
        </Badge>
        {algumFiltroAplicado && (
          <Button type="text" icon={<ClearOutlined />} onClick={handleLimparFiltros}>
            Limpar
          </Button>
        )}
      </div>

      {algumFiltroAplicado && (
        <div className="portal-professor__filtros-chips">
          {chipsFiltrosAplicados.map((texto) => (
            <Tag key={texto}>{texto}</Tag>
          ))}
        </div>
      )}

      {renderConteudo()}

      <Drawer
        title="Filtros"
        placement="bottom"
        height={420}
        open={filtrosDrawerAberto}
        onClose={() => setFiltrosDrawerAberto(false)}
      >
        <div className="portal-professor__filtro-campo">
          <Text type="secondary" style={{ fontSize: 12 }}>Data inicial</Text>
          <DatePicker
            format="DD/MM/YYYY"
            value={filtroDataInicial}
            onChange={setFiltroDataInicial}
            style={{ width: '100%' }}
            placeholder="Data inicial"
            allowClear
          />
        </div>
        <div className="portal-professor__filtro-campo">
          <Text type="secondary" style={{ fontSize: 12 }}>Data final</Text>
          <DatePicker
            format="DD/MM/YYYY"
            value={filtroDataFinal}
            onChange={setFiltroDataFinal}
            style={{ width: '100%' }}
            placeholder="Data final"
            allowClear
          />
        </div>
        <div className="portal-professor__filtro-campo">
          <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
          <Select
            allowClear
            placeholder="Todos"
            value={filtroStatus}
            onChange={setFiltroStatus}
            style={{ width: '100%' }}
            options={STATUS_OPCOES}
          />
        </div>
        <div className="portal-professor__filtro-campo">
          <Text type="secondary" style={{ fontSize: 12 }}>Turma</Text>
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Todas as turmas"
            loading={carregandoTurmas}
            value={filtroTurmaId}
            onChange={setFiltroTurmaId}
            style={{ width: '100%' }}
          >
            {turmas.map((turma) => (
              <Select.Option key={turma.id} value={turma.id}>
                {turma.nome_turm}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="portal-professor__filtro-acoes">
          <Button type="primary" icon={<FilterOutlined />} onClick={handleFiltrar}>
            Filtrar
          </Button>
          <Button icon={<ClearOutlined />} onClick={handleLimparFiltros}>
            Limpar filtros
          </Button>
        </div>
      </Drawer>

      <Modal
        title="Registrar aula"
        open={modalAberto}
        onOk={() => form.submit()}
        onCancel={fecharModal}
        confirmLoading={salvando}
        okText="Registrar"
        cancelText="Cancelar"
      >
        {erroSalvar && (
          <Alert type="error" showIcon message={erroSalvar} style={{ marginBottom: 16 }} />
        )}
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="id_turma"
            label="Turma"
            rules={[{ required: true, message: 'Selecione a turma' }]}
          >
            <Select
              placeholder={carregandoTurmas ? 'Carregando turmas...' : 'Selecione a turma'}
              loading={carregandoTurmas}
              disabled={carregandoTurmas}
              onChange={handleTurmaChange}
              showSearch
              optionFilterProp="children"
            >
              {turmas.map((turma) => (
                <Select.Option key={turma.id} value={turma.id}>
                  {turma.nome_turm}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="data_aula"
            label="Data da aula"
            rules={[{ required: true, message: 'Informe a data da aula' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="hora_inicio"
            label="Hora início"
            rules={[{ required: true, message: 'Informe a hora de início' }]}
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="hora_fim"
            label="Hora fim"
            rules={[{ required: true, message: 'Informe a hora de fim' }]}
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="observacoes" label="Observações">
            <TextArea rows={3} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PortalProfessorAulasDadas;
